import type {LngLat} from '@yandex-delivery/blocks'

/**
 * Функция для преобразования километров в градусы по широте
 * @param km Километры
 */
export function kmToLatitudeDegrees(km: number) {
  return km / 111
}

/**
 * Функция для преобразования километров в градусы по долготе
 * @param km  Километры
 * @param latitude  Широта
 */
export function kmToLongitudeDegrees(km: number, latitude: number) {
  return km / (111 * Math.cos(latitude * (Math.PI / 180)))
}

/**
 * Вычисляет расстояние между двумя точками в километрах.
 * @param lat1 Широта первой точки
 * @param lng1 Долгота первой точки
 * @param lat2 Широта второй точки
 * @param lng2 Долгота второй точки
 * @returns Расстояние между точками в километрах
 */
export function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number) {
  const R = 6371 // Радиус Земли в км
  const dLat = (lat2 - lat1) * (Math.PI / 180)
  const dLng = (lng2 - lng1) * (Math.PI / 180)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * Math.sin(dLng / 2) * Math.sin(dLng / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return R * c
}

/**
 * На основе координат высчитывает левую верхнюю и нижнюю правую точку, которые служат определением видимой части карты.
 * @param coordinates Координаты точек, отображаемых на карте
 * @returns bounds - две точки на карте
 */
export const calculateBounds = (coordinates: LngLat[]): [LngLat, LngLat] => {
  // Инициализируем минимальные и максимальные значения первыми координатами
  let minLng = coordinates[0][0]
  let maxLng = coordinates[0][0]
  let minLat = coordinates[0][1]
  let maxLat = coordinates[0][1]

  // Проходим по всем координатам, обновляя минимальные и максимальные значения
  coordinates.forEach(([lng, lat]) => {
    if (lat < minLat) {
      minLat = lat
    }

    if (lat > maxLat) {
      maxLat = lat
    }

    if (lng < minLng) {
      minLng = lng
    }

    if (lng > maxLng) {
      maxLng = lng
    }
  })

  // Вычисляем центральную широту для точного вычисления км в градусы долготы
  const centerLat = (minLat + maxLat) / 2

  // Вычисляем расстояние между минимальными и максимальными точками
  const distance = calculateDistance(minLat, minLng, maxLat, maxLng)

  // Вычисляем дополнительное расстояние на основе вычисленного расстояния
  const additionalKm = distance * 0.35

  // Преобразуем дополнительное расстояние в градусы широты и долготы
  const lngPadding = kmToLongitudeDegrees(additionalKm, centerLat)
  const latPadding = kmToLatitudeDegrees(additionalKm)

  // Вычисляем левые верхние и правые нижние углы с добавленной границей
  const topLeft: LngLat = [minLng - lngPadding, maxLat + latPadding]
  const bottomRight: LngLat = [maxLng + lngPadding, minLat - latPadding]

  return [topLeft, bottomRight]
}
